import React, { useState, useEffect } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// AI LOADING PAGE — shown while Claude API processes the profile
// ─────────────────────────────────────────────────────────────────────────────

const MESSAGES = [
  "Scanning your educational background...",
  "Analyzing college placement data...",
  "Calculating metro proximity score...",
  "Comparing with 10,000+ career profiles...",
  "Running financial feasibility analysis...",
  "Detecting skill gaps vs industry demand...",
  "Mapping local & global job market trends...",
  "Generating personalized 6-month roadmap...",
  "Computing salary growth projections...",
  "Calculating your success probability...",
  "Finalizing AI career prediction...",
  "Almost ready — compiling your report...",
];

const STAGES = [
  { label: "Profile Parsing",      duration: 15 },
  { label: "Career Matching",       duration: 30 },
  { label: "Market Analysis",       duration: 20 },
  { label: "Roadmap Generation",    duration: 25 },
  { label: "Report Compilation",    duration: 10 },
];

export default function LoadingPage() {
  const [msgIdx,    setMsgIdx]    = useState(0);
  const [stageIdx,  setStageIdx]  = useState(0);
  const [stageProgress, setStageProgress] = useState(0);
  const [dots, setDots] = useState("");

  // Cycle messages
  useEffect(() => {
    const t = setInterval(() => setMsgIdx(i => (i + 1) % MESSAGES.length), 1800);
    return () => clearInterval(t);
  }, []);

  // Animate dots
  useEffect(() => {
    const t = setInterval(() => setDots(d => d.length >= 3 ? "" : d + "."), 500);
    return () => clearInterval(t);
  }, []);

  // Animate stage progress
  useEffect(() => {
    let prog = 0;
    const t = setInterval(() => {
      prog += 2;
      setStageProgress(prog);
      if (prog >= 100) {
        prog = 0;
        setStageIdx(i => (i + 1) % STAGES.length);
      }
    }, 60);
    return () => clearInterval(t);
  }, []);

  return (
    <div style={{
      minHeight: "calc(100vh - 64px)",
      display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "#060d1a",
      padding: "40px 20px",
      gap: 36,
    }}>
      {/* ── Spinning AI Ring ── */}
      <div style={{ position: "relative", width: 140, height: 140 }}>
        {/* Outer ring */}
        <div style={{
          position: "absolute", inset: 0, borderRadius: "50%",
          border: "3px solid transparent",
          borderTopColor: "#22d3ee",
          borderRightColor: "#3b82f6",
          animation: "rotate-ring 1s linear infinite",
        }} />
        {/* Middle ring */}
        <div style={{
          position: "absolute", inset: 14, borderRadius: "50%",
          border: "2px solid transparent",
          borderBottomColor: "#a855f7",
          borderLeftColor: "#10b981",
          animation: "rotate-ring 1.4s linear infinite reverse",
        }} />
        {/* Inner glow */}
        <div style={{
          position: "absolute", inset: 28, borderRadius: "50%",
          background: "radial-gradient(circle,rgba(34,211,238,0.1) 0%,transparent 70%)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 36,
        }}>
          🧠
        </div>
      </div>

      {/* ── Title ── */}
      <div style={{ textAlign: "center" }}>
        <h2 style={{
          fontFamily: "Rajdhani, sans-serif",
          fontSize: 30, fontWeight: 700,
          background: "linear-gradient(90deg,#22d3ee,#3b82f6,#a855f7)",
          WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
          letterSpacing: 2, marginBottom: 10,
          textTransform: "uppercase",
        }}>
          AI Processing{dots}
        </h2>
        <p style={{
          color: "#6b8aad", fontSize: 15,
          fontFamily: "Exo 2, sans-serif",
          minHeight: 24, transition: "all 0.3s",
          maxWidth: 400, textAlign: "center",
        }}>
          {MESSAGES[msgIdx]}
        </p>
      </div>

      {/* ── Stage Progress Bars ── */}
      <div style={{ width: "100%", maxWidth: 440, display: "flex", flexDirection: "column", gap: 14 }}>
        {STAGES.map((stage, i) => {
          const done    = i < stageIdx;
          const active  = i === stageIdx;
          const pending = i > stageIdx;
          const pct     = done ? 100 : active ? stageProgress : 0;
          const color   = done ? "#10b981" : active ? "#22d3ee" : "#2d4a66";

          return (
            <div key={stage.label}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <span style={{
                  fontSize: 12, fontFamily: "Exo 2, sans-serif",
                  color: done ? "#10b981" : active ? "#22d3ee" : "#3d5a74",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  {done ? "✓ " : active ? "⟳ " : "○ "}{stage.label}
                </span>
                <span style={{ fontSize: 12, fontFamily: "Rajdhani, sans-serif", color, fontWeight: 700 }}>
                  {pct}%
                </span>
              </div>
              <div style={{ height: 3, background: "rgba(34,211,238,0.07)", borderRadius: 3 }}>
                <div style={{
                  height: "100%", borderRadius: 3,
                  width: `${pct}%`,
                  background: `linear-gradient(90deg,${color},${color}88)`,
                  transition: "width 0.1s linear",
                  boxShadow: active ? `0 0 8px ${color}55` : "none",
                }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Disclaimer ── */}
      <p style={{
        fontSize: 12, color: "#3d5a74",
        fontFamily: "Exo 2, sans-serif",
        textAlign: "center", maxWidth: 380,
      }}>
        Our AI is cross-referencing your profile with real-time market data, college placement records and metro job demand. This may take 10–30 seconds.
      </p>
    </div>
  );
}